import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '@/hooks/useAuth';
import { getAuthErrorMessage } from '@/utils/authErrors';
import { emailValidator, passwordValidator } from '@/lib/forms';

export function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state as any)?.from?.pathname || '/';

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const form = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
        onSubmit: async ({ value }) => {
            setError('');
            setIsLoading(true);

            try {
                await login(value.email, value.password);
                navigate(from, { replace: true });
            } catch (err: any) {
                const msg = getAuthErrorMessage(err);
                setError(msg);
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <div className="container d-flex justify-content-center align-items-center min-vh-100 py-5">
            <div className="card shadow border-0" style={{ maxWidth: 400, width: '100%' }}>
                <div className="card-body p-4">
                    <div className="text-center mb-4">
                        <h1 className="h3 mb-3 fw-normal">Welcome Back</h1>
                        <p className="text-muted">Sign in to continue to PlaylistR</p>
                    </div>

                    {error && <div className="alert alert-danger" role="alert">{error}</div>}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <form.Field
                            name="email"
                            validators={{
                                onChange: emailValidator,
                            }}
                        >
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="emailInput" className="form-label">Email address</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="emailInput"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="name@example.com"
                                        autoFocus
                                    />
                                    {field.state.meta.errors.length > 0 && (
                                        <div className="text-danger small mt-1">
                                            {field.state.meta.errors[0]}
                                        </div>
                                    )}
                                </div>
                            )}
                        </form.Field>
                        <form.Field
                            name="password"
                            validators={{
                                onChange: passwordValidator,
                            }}
                        >
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="passwordInput" className="form-label">Password</label>
                                    <div className="input-group">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            className="form-control"
                                            id="passwordInput"
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            placeholder="Password"
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                        </button>
                                    </div>
                                    {field.state.meta.errors.length > 0 && (
                                        <div className="text-danger small mt-1">
                                            {field.state.meta.errors[0]}
                                        </div>
                                    )}
                                </div>
                            )}
                        </form.Field>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="rememberMe" />
                                <label className="form-check-label" htmlFor="rememberMe">
                                    Remember me
                                </label>
                            </div>
                            <Link to="/forgot-password" className="text-decoration-none small">Forgot password?</Link>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-100 py-2"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Signing in...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-3">
                        <p className="small text-muted mb-0">
                            Don't have an account? <Link to="/register" className="text-decoration-none">Register</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
